import React from 'react';
import { motion } from 'framer-motion';
import { 
  BarChart, 
  Headphones, 
  Eye, 
  Users, 
  Newspaper,
  Calendar,
  Play,
  Mail,
  Globe,
  Linkedin
} from 'lucide-react';
import type { TeamMember } from '../../hooks/useTeamMembers';
import type { PodcastWithRelations } from '../../hooks/usePodcasts';
import type { NewsArticleWithRelations } from '../../types/news';

interface DashboardProps {
  podcasts: PodcastWithRelations[];
  members: TeamMember[];
  newsArticles: NewsArticleWithRelations[];
  memberPercentage: number;
  newsPercentage: number;
  isPositiveGrowth: boolean;
  isNewsPositiveGrowth: boolean;
}

const StatCard = ({ 
  title, 
  value, 
  icon: Icon, 
  percentage, 
  isPositive, 
  delay 
}: { 
  title: string;
  value: number;
  icon: any;
  percentage: number;
  isPositive: boolean;
  delay: number;
}) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay }}
    whileHover={{ y: -12, transition: { duration: 0.2 } }}
    className="bg-gradient-to-br from-card/20 to-card/10 p-6 rounded-xl shadow-lg border border-white/10 hover:border-primary/50 transition-all duration-300 shadow-lg hover:shadow-2xl hover:shadow-primary/20 group"
  >
    <div className="flex items-center justify-between">
      <div>
        <p className="text-sm text-gray-400">{title}</p>
        <h3 className="text-3xl font-bold mt-1">{value}</h3>
      </div>
      <div className="p-3 bg-primary/10 rounded-full">
        <Icon className="w-6 h-6 text-primary" />
      </div>
    </div>
    <div className="mt-4 flex items-center text-sm">
      <BarChart className={`w-4 h-4 ${isPositive ? 'text-green-500' : 'text-red-500'} mr-1`} />
      <span className={isPositive ? 'text-green-500' : 'text-red-500'}>
        {isPositive ? '+' : ''}{percentage}%
      </span>
      <span className="text-gray-400 ml-2">from last month</span>
    </div>
  </motion.div>
);

const RecentCard = ({ 
  title, 
  icon: Icon, 
  items, 
  renderItem 
}: { 
  title: string;
  icon: any;
  items: any[];
  renderItem: (item: any) => React.ReactNode;
}) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: 0.5 }}
    whileHover={{ y: -12, transition: { duration: 0.2 } }}
    className="bg-[#0A0A0A] rounded-xl p-6 shadow-lg border border-white/10 hover:border-primary/50 transition-all duration-300 shadow-lg hover:shadow-2xl hover:shadow-primary/20"
  >
    <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
      <Icon className="w-5 h-5 text-primary" />
      {title}
    </h3>
    {items.length > 0 ? (
      <div className="space-y-4">
        {items.slice(0, 5).map(renderItem)}
      </div>
    ) : (
      <p className="text-gray-400">Sem {title.toLowerCase()} recentes</p>
    )}
  </motion.div>
);

export const Dashboard: React.FC<DashboardProps> = ({
  podcasts,
  members,
  newsArticles,
  memberPercentage,
  newsPercentage,
  isPositiveGrowth,
  isNewsPositiveGrowth
}) => {
  if (!podcasts || !members || !newsArticles) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-16 w-16 border-4 border-primary border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Stats Cards */}
        <StatCard
          title="Total Podcasts"
          value={podcasts.length}
          icon={Headphones}
          percentage={12}
          isPositive={true}
          delay={0.1}
        />
        <StatCard
          title="Total de Visualizações"
          value={podcasts.reduce((total, podcast) => total + podcast.plays, 0)}
          icon={Eye}
          percentage={18}
          isPositive={true}
          delay={0.2}
        />
        <StatCard
          title="Membros da Equipa"
          value={members.length}
          icon={Users}
          percentage={memberPercentage}
          isPositive={isPositiveGrowth}
          delay={0.3}
        />
        <StatCard
          title="Artigos de Notícias"
          value={newsArticles.length}
          icon={Newspaper}
          percentage={newsPercentage}
          isPositive={isNewsPositiveGrowth}
          delay={0.4}
        />
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <RecentCard
          title="Podcasts Recentes"
          icon={Headphones}
          items={podcasts.slice(0, 5)}
          renderItem={(podcast) => (
            <motion.div 
              key={podcast.id}
              whileHover={{ y: -4, transition: { duration: 0.2 } }}
              className="flex items-center gap-4 p-3 rounded-lg hover:bg-primary/5 transition-all duration-300 border border-transparent hover:border-primary/20 hover:shadow-lg hover:shadow-primary/10"
            >
              <div className="w-16 h-16 rounded-lg overflow-hidden shadow-md">
                <img src={podcast.image_url || ''} alt={podcast.title} className="w-full h-full object-cover" />
              </div>
              <div className="flex-1">
                <h4 className="font-medium group-hover:text-primary transition-colors">{podcast.title}</h4>
                <p className="text-sm text-gray-400">{podcast.author}</p>
              </div>
              <div className="text-sm text-gray-400">
                {new Date(podcast.created_at).toLocaleDateString()}
              </div>
            </motion.div>
          )}
        />

        <RecentCard
          title="Notícias Recentes"
          icon={Newspaper}
          items={newsArticles.slice(0, 5)}
          renderItem={(article) => (
            <motion.div 
              key={article.id}
              whileHover={{ y: -4, transition: { duration: 0.2 } }}
              className="flex items-center gap-4 p-3 rounded-lg hover:bg-primary/5 transition-all duration-300 border border-transparent hover:border-primary/20 hover:shadow-lg hover:shadow-primary/10"
            >
              <div className="w-16 h-16 rounded-lg overflow-hidden shadow-md">
                <img src={article.cover_image || ''} alt={article.title} className="w-full h-full object-cover" />
              </div>
              <div className="flex-1">
                <h4 className="font-medium group-hover:text-primary transition-colors">{article.title}</h4>
                <p className="text-sm text-gray-400">{article.excerpt}</p>
              </div>
              <div className="text-sm text-gray-400">
                {new Date(article.published_at || '').toLocaleDateString()}
              </div>
            </motion.div>
          )}
        />
      </div>
    </div>
  );
}; 